import { Button } from 'components/Button'
import { reloadToRoot } from 'lib/utils'
import { Check, Loader2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { BusinessMembersService } from 'openapi/services/BusinessMembersService'
import type { BusinessMembershipEntity } from 'openapi/models/BusinessMembershipEntity'
import { switchBusinessAction } from 'modules/business'
import { useAppDispatch } from 'modules/core'

export function PendingInvitationsBanner() {
  const dispatch = useAppDispatch()
  const [invitations, setInvitations] = useState<BusinessMembershipEntity[]>([])
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  const fetchInvitations = useCallback(async () => {
    try {
      const response =
        await BusinessMembersService.businessMembershipsControllerFindMyInvitationsV1()
      setInvitations(response.data)
    } catch {
      // Silently fail — this is a non-critical enhancement
    }
  }, [])

  useEffect(() => {
    fetchInvitations()
  }, [fetchInvitations])

  const handleAccept = async (membershipId: string) => {
    setAcceptingId(membershipId)
    try {
      const membership =
        await BusinessMembersService.businessMembershipsControllerAcceptInvitationV1(membershipId)
      setInvitations(prev => prev.filter(i => i.id !== membershipId))
      // Auto-switch into the freshly accepted business. The reload that
      // switchBusinessAction triggers re-bootstraps the entity list from the
      // server, so we don't need a separate findMyBusinesses dispatch.
      if (membership.businessId) {
        try {
          await dispatch(switchBusinessAction({ businessId: membership.businessId })).unwrap()
          reloadToRoot()
        } catch (err) {
          console.warn('Accepted invite but auto-switch failed:', err)
        }
      }
    } catch (err) {
      console.error('Failed to accept invitation:', err)
    } finally {
      setAcceptingId(null)
    }
  }

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => new Set([...prev, id]))
  }

  const visible = invitations.filter(i => !dismissedIds.has(i.id))
  if (visible.length === 0) return null

  return (
    <div className="border-b border-line bg-bg-2">
      {visible.map(invitation => (
        <div
          key={invitation.id}
          className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-3 sm:px-6"
        >
          <p className="text-[13px] text-paper-dim">
            <span className="font-medium text-paper">
              {invitation.invitedBy?.name || 'Someone'}
            </span>
            {' invited you to join '}
            <span className="font-medium text-paper">
              {invitation.business?.name || 'a business'}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="t3-primary"
              size="t3-sm"
              onClick={() => handleAccept(invitation.id)}
              disabled={acceptingId === invitation.id}
              className="!h-8 gap-1.5 !rounded-[8px] px-3 text-[12px]"
            >
              {acceptingId === invitation.id ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Accept
            </Button>
            <button
              onClick={() => handleDismiss(invitation.id)}
              aria-label="Dismiss invitation"
              className="rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
